import React, { useContext, useState } from 'react';
import { Percent, Loader2, Info, FileText, User, ChevronDown, ArrowRightLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { TabItem as TabItemType } from '@/types';
import { useCurrency } from '@/hooks/useCurrency';
import { useIvaPricing } from '@/hooks/useIvaPricing';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { useHasRole } from '@/hooks/useHasRole';
import { useIsMobile } from '@/hooks/use-mobile';
import { getDiscountValidation } from '@/lib/discountValidation';
import { useIsPhysicalTabletDetection } from '@/hooks/useIsPhysicalTabletDetection';
import { useTabItemActions } from '@/hooks/tabs/useTabItemActions';
import { useTabCalculations } from '@/hooks/tabs/useTabCalculations';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { formatTime, parseTimestamp } from '@/utils/dateFormat';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useTabsViewMode } from '@/hooks/useTabsViewMode';
import { FEATURE_FLAGS } from '@/types/featureFlags';
import TabItemQuantityDialog from './TabItemQuantityDialog';
import { formatQtyDisplay, formatWeight, isWeightLine } from '@/utils/weightPricing';

interface TabItemProps {
  item: TabItemType;
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onUpdateWeight?: (itemId: string, weightKg: number) => void;
  discountInputs: { [key: string]: string };
  setDiscountInputs: (value: React.SetStateAction<{ [key: string]: string }>) => void;
  isUpdatingQuantity?: boolean;
  onMoveItem?: (item: TabItemType) => void;
  canMoveItem?: boolean;
}

const TabItem: React.FC<TabItemProps> = ({
  item,
  onUpdateQuantity,
  onUpdateWeight,
  discountInputs,
  setDiscountInputs,
  isUpdatingQuantity = false,
  onMoveItem,
  canMoveItem = false,
}) => {
  const { formatAmount } = useCurrency();
  const { showIvaPrice, getPriceWithIva } = useIvaPricing();
  const { translator, userLanguage } = useContext(LanguageProviderContext);
  const { currentRole } = useHasRole();
  const isMobile = useIsMobile();
  const isTablet = useIsPhysicalTabletDetection();
  const [promoIncludedOpen, setPromoIncludedOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const discountValue = parseFloat(discountInputs[item.id] || '0');
  const { validation, maxDiscountAllowed } = getDiscountValidation(discountValue, currentRole);
  const { applyItemDiscount } = useTabItemActions();
  const { calculateItemTotal } = useTabCalculations();
  const { isEnabled } = useFeatureFlag();
  const { viewMode } = useTabsViewMode();
  const isCustomerPositionEnabled = isEnabled(FEATURE_FLAGS.CUSTOMER_POSITIONS) && viewMode === 'tables';
  const hasNotes = item.notes && item.notes.trim().length > 0;
  const hasCustomerPosition = isCustomerPositionEnabled && item.customerPosition != null;
  const hasDiscount = item.discount > 0;
  const itemCreatedAt = parseTimestamp(item.createdAt);
  const [isQuantityModalOpen, setIsQuantityModalOpen] = useState(false);
  const isWeight = isWeightLine(item);

  const handleOpenQuantityModal = () => {
    if (isUpdatingQuantity) return;
    setIsQuantityModalOpen(true);
  };

  const handleQuantityConfirm = (newValue: number) => {
    if (isWeight) {
      onUpdateWeight?.(item.id, newValue);
      return;
    }
    onUpdateQuantity(item.id, newValue);
  };

  return (
    <Collapsible
      open={isDetailsOpen}
      onOpenChange={setIsDetailsOpen}
      className="pt-2 px-2 sm:pt-3 sm:px-3 pb-1 bg-muted rounded-lg space-y-2 sm:space-y-3"
    >
      <div className="flex justify-between items-start sm:items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <CollapsibleTrigger asChild>
              <h4 className="font-medium text-sm sm:text-base truncate hover:cursor-pointer">{item.product.name}</h4>
            </CollapsibleTrigger>
            {item.product.stock <= 0 && !item.product.avoidTracking && (
              isMobile || isTablet ? (
                <Dialog>
                  <DialogTrigger asChild>
                    <button className="touch-manipulation flex-shrink-0">
                      <Info className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground hover:text-foreground cursor-help" />
                    </button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <div className="flex items-start gap-4">
                      <Info className="h-6 w-6 text-muted-foreground flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-medium text-base mb-4">{translator('Tabs.outOfStockTooltipTitle')}</h3>
                        <p className="text-base text-muted-foreground">{translator('Tabs.outOfStockTooltip')}</p>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-5 w-5 text-muted-foreground hover:text-foreground cursor-help flex-shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-sm">{translator('Tabs.outOfStockTooltip')}</p>
                  </TooltipContent>
                </Tooltip>
              )
            )}
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground truncate">
            {isWeight
              ? `${formatWeight(item.weightKg!)} · ${formatAmount(item.unitPrice)}`
              : `${formatAmount(item.unitPrice)} ${translator('Tabs.eachItem')}`}
            {showIvaPrice && ` · ${formatAmount(getPriceWithIva(item.unitPrice))} ${translator('Global.ivaIncluded')}`}
          </p>
          {item.modifiers && item.modifiers.length > 0 && (
            <p className="text-xs text-muted-foreground truncate">
              {item.modifiers.map((modifier) => `+ ${modifier.name}`).join(' · ')}
            </p>
          )}
          {item.promotionComponents && item.promotionComponents.length > 0 && (
            <Collapsible open={promoIncludedOpen} onOpenChange={setPromoIncludedOpen} className="mt-1">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-2 rounded-md border border-dashed px-2 py-1 text-left text-xs text-muted-foreground hover:bg-background/60 touch-manipulation"
                >
                  <span>{translator('Promotions.whatsIncluded')}</span>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 shrink-0 transition-transform',
                      promoIncludedOpen && 'rotate-180'
                    )}
                  />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-1">
                <div className="flex flex-wrap gap-1">
                  {[...item.promotionComponents]
                    .sort((a, b) => a.productName.localeCompare(b.productName) || a.id.localeCompare(b.id))
                    .map((c) => (
                      <Badge key={c.id} variant="secondary" className="text-xs font-normal">
                        {translator('Promotions.componentLine', {
                          name: c.productName,
                          qty: String(c.quantity),
                        })}
                      </Badge>
                    ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
        <div className="flex items-center gap-1.5 sm:space-x-3 flex-shrink-0">
          {canMoveItem && onMoveItem && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onMoveItem(item)}
              disabled={isUpdatingQuantity}
              className="h-7 w-7 px-0 sm:h-9 sm:w-9"
              aria-label={translator('Tabs.moveItem')}
              title={translator('Tabs.moveItem')}
            >
              <ArrowRightLeft className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={handleOpenQuantityModal}
            disabled={isUpdatingQuantity}
            className="h-7 min-w-7 px-2 sm:h-9 sm:min-w-9 sm:px-3"
            aria-label={translator('Tabs.adjustQuantity')}
          >
            {isUpdatingQuantity ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <span className="text-base sm:text-lg font-bold tabular-nums">
                {formatQtyDisplay(isWeight ? item.weightKg! : item.quantity, isWeight)}
              </span>
            )}
          </Button>
          <div className="min-w-[55px] sm:w-20 text-right">
            <span className="font-medium text-sm sm:text-base">
              {formatAmount(calculateItemTotal(item))}
            </span>
            {showIvaPrice && (
              <span className="block text-[10px] leading-tight text-muted-foreground">
                {formatAmount(getPriceWithIva(calculateItemTotal(item)))} {translator('Global.ivaIncluded')}
              </span>
            )}
          </div>
          <CollapsibleTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              disabled={isUpdatingQuantity}
              className="h-7 w-7 p-0 hover:cursor-pointer"
              aria-label={translator('Tabs.adjustDiscount')}
            >
              <ChevronDown
                className={cn(
                  'h-4 w-4 shrink-0 transition-transform',
                  isDetailsOpen && 'rotate-180'
                )}
              />
            </Button>
          </CollapsibleTrigger>
        </div>
      </div>

      {(hasNotes || hasCustomerPosition || hasDiscount) && (
        <div className="flex items-center gap-3">
          {hasCustomerPosition && (
            <div className="relative w-fit text-muted-foreground">
              <User className="h-4 w-4" />
              <div className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-red-500 border border-background" />
            </div>
          )}
          {hasNotes && (
            <div className="relative w-fit text-muted-foreground">
              <FileText className="h-4 w-4" />
              <div className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-red-500 border border-background" />
            </div>
          )}
          {hasDiscount && (
            <div className="relative w-fit text-muted-foreground">
              <Percent className="h-4 w-4" />
              <div className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-red-500 border border-background" />
            </div>
          )}
        </div>
      )}

      <CollapsibleContent className="space-y-2 sm:space-y-3 pt-2">
        <div className="flex items-center gap-1.5 sm:space-x-2">
          <Label htmlFor={`item-discount-${item.id}`} className="text-xs whitespace-nowrap">
            {translator('Tabs.itemDiscount')} (%):
          </Label>
          <Input
            id={`item-discount-${item.id}`}
            type="number"
            value={discountInputs[item.id] || ''}
            hideSpinners={true}
            onChange={(e) => setDiscountInputs((prev) => ({
              ...prev,
              [item.id]: e.target.value,
            }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (validation.isValid) {
                  applyItemDiscount(item.id);
                }
              }
            }}
            placeholder="0"
            className={`w-14 sm:w-16 h-7 text-xs ${!validation.isValid ? 'border-red-500' : ''}`}
            min={0}
            max={maxDiscountAllowed}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => applyItemDiscount(item.id)}
            className="h-7 px-2 text-xs"
            disabled={!validation.isValid}
          >
            <Percent className="h-3 w-3" />
          </Button>
          {item.discount > 0 && (
            <Badge variant="secondary" className="text-xs">
              -{item.discount}%
            </Badge>
          )}
        </div>
        {!validation.isValid && (
          <div className="text-red-500 text-xs">
            {translator('Tabs.maxDiscountAllowed')} {validation.maxAllowed}%
          </div>
        )}
        {hasCustomerPosition && (
          <div className="pt-2 border-t">
            <p className="text-sm text-yellow-500 whitespace-pre-wrap">
              {translator('Tabs.customerPositionLine', { number: String(item.customerPosition) })}
            </p>
          </div>
        )}
        {hasNotes && (
          <div className="pt-2 border-t">
            <p className="text-sm text-yellow-500 whitespace-pre-wrap">{item.notes}</p>
          </div>
        )}
        {itemCreatedAt && (
          <div className="flex justify-end">
            <span className="text-xs text-muted-foreground tabular-nums">
              {translator('Tabs.timeElapsed')}: {formatTime(itemCreatedAt, userLanguage)}
            </span>
          </div>
        )}
      </CollapsibleContent>

      <TabItemQuantityDialog
        key={`${item.id}-${item.quantity}-${item.weightKg ?? ''}-${isQuantityModalOpen}`}
        isOpen={isQuantityModalOpen}
        onClose={() => setIsQuantityModalOpen(false)}
        currentQuantity={isWeight ? item.weightKg! : item.quantity}
        productName={item.product.name}
        isSoldByWeight={isWeight}
        onConfirm={handleQuantityConfirm}
      />
    </Collapsible>
  );
};

export default TabItem;
